"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { CalendarRange, ChartLine as LineChart, ShieldAlert, ArrowUpRight, Sun, CloudRain, Snowflake, Compass } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
const challenges = [{
  id: 1,
  title: "Seasonality & Window Compression",
  subtitle: "Demand management",
  iconName: "CalendarRange",
  description: "Manage sharp peak leisure surges during Gujarat holidays and summer heat waves against shoulder dips. We build dynamic minimum length-of-stay controls and pace-based pricing so your property never relies on panic discounting.",
  metricLabel: "Strategy focus",
  metricValue: "Pace-based pacing & minimum stay restrictions"
}, {
  id: 2,
  title: "Aggressive Comp-Set Undercutting",
  subtitle: "Real-time rate intelligence",
  iconName: "LineChart",
  description: "Track intraday rate shifts across MakeMyTrip, Agoda, and Booking.com for Nakki Lake and hill-view competitors. Automated alerts and yield adjustments protect your RevPAR while securing high-intent bookings ahead of market drops.",
  metricLabel: "Strategy focus",
  metricValue: "Intraday OTA monitoring & rule-based rate fencing"
}, {
  id: 3,
  title: "The OTA Commission & Parity Trap",
  subtitle: "Channel distribution",
  iconName: "ShieldAlert",
  description: "Stop unauthorized B2B wholesaler leakage and reclaim margins lost to steep OTA commissions. We enforce strict rate parity across meta-search and drive direct guest conversions through targeted booking incentives.",
  metricLabel: "Strategy focus",
  metricValue: "Wholesaler rate fencing & direct channel boost"
}];
const seasonalityFactors = [{
  id: 1,
  season: "Summer Rush & Diwali",
  period: "May - June & Oct - Nov",
  impact: "High demand from Gujarat & Rajasthan",
  strategy: "Premium ADR positioning with non-refundable rate tiers"
}, {
  id: 2,
  season: "Monsoon Retreat",
  period: "July - September",
  impact: "Steady weekend leisure flow seeking greenery",
  strategy: "Experience-led packages and flexible cancellation fences"
}, {
  id: 3,
  season: "Winter Hilltop Peak",
  period: "December - February",
  impact: "Chilly weather getaways & year-end celebrations",
  strategy: "Length-of-stay minimums (2-3 nights) for festive yield"
}, {
  id: 4,
  season: "Weekend vs. Midweek",
  period: "Year-round pattern",
  impact: "Severe weekend compression (Fri-Sun) vs slow weekdays",
  strategy: "Midweek corporate and wellness packages to balance occupancy"
}];
export default function Landscape() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {},
    show: {
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.15
      }
    }
  };
  const cardVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="hospitalitylandscapemountabu" data-nav="light" className="relative bg-background py-20 md:py-28 text-foreground">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Section Header */}
        <div className="mx-auto max-w-3xl text-center mb-16 md:mb-20">
          <Badge variant="outline" className="mb-4 border-secondary/40 text-secondary-foreground">
            Market Dynamics & Yield Protection
          </Badge>
          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-foreground">
            Navigating the hospitality landscape of Mount Abu
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
            Mount Abu presents a distinct hill-station micro-market shaped by compressed drive-in booking windows, sharp seasonal surges from Gujarat and Rajasthan, and aggressive OTA rate battles. Sunil Singh structures disciplined revenue models designed specifically for hill resorts, heritage stays, and boutique hotels.
          </p>
        </div>

        {/* 3-Column Card Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.15
      }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8 mb-16">
          {challenges.map((item, index) => <motion.div key={item.id} data-index={index} variants={cardVariants} className="h-full flex">
              <Card className="flex flex-col justify-between w-full border border-border bg-card shadow-sm hover:shadow-md transition-[border-color,box-shadow] duration-300">
                <CardHeader className="gap-3">
                  <div className="flex items-center justify-between">
                    <div className="inline-flex size-12 items-center justify-center rounded-md bg-secondary/10 text-secondary-foreground">
                      {item.iconName === "CalendarRange" && <CalendarRange className="size-6 text-chart-2" />}
                      {item.iconName === "LineChart" && <LineChart className="size-6 text-chart-2" />}
                      {item.iconName === "ShieldAlert" && <ShieldAlert className="size-6 text-chart-2" />}
                    </div>
                    <span className="text-xs font-mono font-medium text-muted-foreground tracking-wider uppercase">
                      0{index + 1}
                    </span>
                  </div>
                  <div>
                    <span className="text-xs font-semibold uppercase tracking-wider text-secondary">
                      {item.subtitle}
                    </span>
                    <CardTitle className="font-serif text-xl sm:text-2xl font-bold mt-1.5 text-foreground leading-snug">
                      {item.title}
                    </CardTitle>
                  </div>
                </CardHeader>

                <CardContent className="space-y-6 pt-2">
                  <CardDescription className="text-sm leading-relaxed text-muted-foreground">
                    {item.description}
                  </CardDescription>

                  <div className="rounded-md border border-border/60 bg-muted/40 p-3.5 text-xs">
                    <p className="font-semibold text-foreground mb-1">
                      {item.metricLabel}
                    </p>
                    <p className="text-muted-foreground">
                      {item.metricValue}
                    </p>
                  </div>
                </CardContent>
              </Card>
            </motion.div>)}
        </motion.div>

        {/* Seasonality Block */}
        <div className="rounded-xl border border-border bg-card p-6 sm:p-8 shadow-sm">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 pb-6 border-b border-border/70">
            <div>
              <span className="text-xs font-semibold uppercase tracking-wider text-secondary">
                Mount Abu Demand Cycles
              </span>
              <h3 className="font-serif text-xl sm:text-2xl font-bold text-foreground mt-1">
                Seasonal Yield Strategy Matrix
              </h3>
            </div>
            <p className="text-sm text-muted-foreground max-w-md">
              Data-backed rate controls tailored to the specific arrival patterns of Rajasthan&apos;s premier hill station.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
            {seasonalityFactors.map((item, idx) => <div key={item.id} data-index={idx} className="p-4 rounded-lg bg-muted/30 border border-border/60 flex flex-col justify-between gap-3">
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    {idx === 0 && <Sun className="size-4 text-chart-2" />}
                    {idx === 1 && <CloudRain className="size-4 text-chart-2" />}
                    {idx === 2 && <Snowflake className="size-4 text-chart-2" />}
                    {idx === 3 && <Compass className="size-4 text-chart-2" />}
                    <span className="text-xs font-semibold uppercase tracking-wider text-secondary">
                      {item.period}
                    </span>
                  </div>
                  <h4 className="font-semibold text-base text-foreground mb-1">
                    {item.season}
                  </h4>
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    {item.impact}
                  </p>
                </div>
                <div className="pt-2 border-t border-border/50 text-xs">
                  <span className="font-medium text-foreground block mb-0.5">Tactic:</span>
                  <span className="text-muted-foreground">{item.strategy}</span>
                </div>
              </div>)}
          </div>
        </div>

        {/* Section Bottom CTA / Footnote */}
        <div className="mt-14 md:mt-18 pt-8 border-t border-border/70 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-sm text-muted-foreground text-center sm:text-left">
            Need a tailored revenue assessment for your Mount Abu hotel or boutique stay?
          </p>
          <Button asChild variant="default" className="bg-primary text-primary-foreground hover:bg-primary/90">
            <Link to="#" className="inline-flex items-center gap-2">
              <span>Book a Revenue Audit</span>
              <ArrowUpRight className="size-4" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}